import { Controller } from 'stimulus';

// The job's Tasks tab as one view (jobs/tasks/index/_one_row, where the new
// Schedule is on). A suggestion chip ("+ Priya?") saves through the
// Schedule's plan_suggestions and leaves a note with Undo (plan_undo). Any
// save re-renders its row from Jobs::TasksController#card (density one):
// the chips' own saves, and the popups' (tasks-popup-editor fires
// tasks:changed). The readiness strip's needs filter (tasks:needs-filter)
// shows only the tasks that need something.
export default class extends Controller {
  static targets = ['note', 'filterLine'];

  connect() {
    this.changed = (e) => { if (e.detail && e.detail.taskId) this.refresh(e.detail.taskId); };
    this.filtered = (e) => this.filter(e.detail || {});
    document.addEventListener('tasks:changed', this.changed);
    document.addEventListener('tasks:needs-filter', this.filtered);
  }

  disconnect() {
    document.removeEventListener('tasks:changed', this.changed);
    document.removeEventListener('tasks:needs-filter', this.filtered);
  }

  use(e) {
    e.preventDefault();
    const btn = e.currentTarget;
    if (btn.disabled) return;
    btn.disabled = true;
    const taskId = btn.dataset.task;
    this.request(this.data.get('suggestUrl'), 'PATCH', { task_ids: [taskId], field: btn.dataset.field })
      .then((json) => {
        this.showNote((json.changes || []).join(' '), json.undo, taskId);
        this.changedTask(taskId);
      })
      .catch((err) => { btn.disabled = false; this.showNote(err.message, null, null, true); });
  }

  undo(e) {
    e.preventDefault();
    if (!this.undoItems) return;
    const { items, taskId } = this.undoItems;
    this.undoItems = null;
    this.request(this.data.get('undoUrl'), 'POST', { undo: items })
      .then(() => {
        this.showNote('Undone.', null, null);
        this.changedTask(taskId);
      })
      .catch((err) => this.showNote(err.message, null, null, true));
  }

  // A row changed: draw it again, and tell the readiness strip
  changedTask(taskId) {
    document.dispatchEvent(new CustomEvent('tasks:changed', { detail: { taskId } }));
  }

  refresh(taskId) {
    const row = this.element.querySelector(`.tov-row[data-id="${taskId}"]`);
    if (!row || !row.dataset.rowUrl) return;
    fetch(row.dataset.rowUrl, { credentials: 'same-origin', headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' } })
      .then((r) => (r.ok && !r.redirected ? r.text() : ''))
      .then((html) => {
        if (html.indexOf('tov-row') < 0 || !row.parentNode) return;
        const holder = document.createElement('div');
        holder.innerHTML = html.trim();
        const fresh = holder.firstElementChild;
        if (!fresh) return;
        fresh.hidden = row.hidden; // (an answered task stays in the filtered list until it's shown again)
        row.replaceWith(fresh);
      })
      .catch(() => {});
  }

  // From the readiness strip: { on, ids } (the tasks that need something)
  filter({ on, ids }) {
    this.needsOnly = !!on;
    this.needIds = new Set((ids || []).map(String));
    const rows = Array.from(this.element.querySelectorAll('.tov-row'));
    rows.forEach((row) => { row.hidden = this.needsOnly && !this.needIds.has(row.dataset.id); });
    if (!this.hasFilterLineTarget) return;
    const shown = rows.filter((r) => !r.hidden).length;
    this.filterLineTarget.hidden = !this.needsOnly;
    this.filterLineTarget.querySelector('span').textContent = `Showing the ${shown} of ${rows.length} tasks that need something.`;
  }

  showAll(e) {
    e.preventDefault();
    document.dispatchEvent(new CustomEvent('tasks:needs-filter', { detail: { on: false, ids: [] } }));
  }

  showNote(text, undo, taskId, error) {
    if (!this.hasNoteTarget) return;
    this.undoItems = undo && undo.length ? { items: undo, taskId } : null;
    const body = error ? `<span class="tov-err">${this.escape(text)}</span>` : this.escape(text);
    this.noteTarget.innerHTML = body + (this.undoItems ? ' <button type="button" class="link focus:outline-none" data-action="tasks-one#undo">Undo</button>' : '');
    this.noteTarget.hidden = false;
  }

  request(url, method, body) {
    const meta = document.querySelector('meta[name="csrf-token"]');
    return fetch(url, {
      method,
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-Token': meta ? meta.content : '' },
      body: JSON.stringify(body)
    })
      .then((r) => r.json().catch(() => ({ ok: false })))
      .then((json) => {
        if (!json.ok) throw new Error(json.error || 'That could not be saved.');
        return json;
      });
  }

  escape(s) {
    return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }
}
